import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Tag, Lock, Check } from "lucide-react";
import { api, gbp, formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import Seo from "@/components/Seo";
import { toast } from "sonner";

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, cartSubtotal, user } = useStore();
  const [step, setStep] = useState(1);
  const [delivery, setDelivery] = useState({ free_threshold: 40, options: [] });
  const [method, setMethod] = useState("standard");
  const [discount, setDiscount] = useState(null);
  const [code, setCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "",
    line1: "", line2: "", city: "", postcode: "", country: "United Kingdom", notes: "",
  });

  useEffect(() => {
    api.get("/content/delivery_settings").then((r) => {
      setDelivery(r.data.data); setMethod(r.data.data.options[0]?.id || "standard");
    });
  }, []);

  useEffect(() => { if (cart.length === 0) navigate("/cart"); }, [cart, navigate]);

  const option = delivery.options.find((o) => o.id === method) || delivery.options[0];
  const freeDelivery = cartSubtotal >= (delivery.free_threshold || 40);
  const deliveryCost = freeDelivery ? 0 : (option?.price || 0);
  const codeAmt = discount?.discount || 0;
  const discountAmt = codeAmt;
  const discountLabel = discount ? `Code ${discount.code}` : null;
  const total = Math.max(0, cartSubtotal - discountAmt) + deliveryCost;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyCode = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.get(`/discounts/validate/${code.trim()}?subtotal=${cartSubtotal}`);
      setDiscount(data); toast.success(`Code applied — ${gbp(data.discount)} off`);
    } catch (e) { setDiscount(null); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const placeOrder = async () => {
    if (!agreed) { toast.error("Please agree to the Terms & Conditions and Privacy Policy."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout", {
        items: cart,
        customer: { name: form.name, email: form.email, phone: form.phone },
        shipping_address: { line1: form.line1, line2: form.line2, city: form.city, postcode: form.postcode, country: form.country },
        delivery_method: method, discount_code: discount?.code || null, order_notes: form.notes,
        agreed_terms: agreed, origin_url: window.location.origin,
      });
      window.location.href = data.url; // Stripe hosted checkout
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSubmitting(false); }
  };

  const canContinue = form.name && form.email && form.line1 && form.city && form.postcode;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Checkout" />
      <h1 className="font-serif-display text-4xl sm:text-5xl mb-8">Checkout</h1>

      <div className="flex items-center gap-2 mb-10 text-xs uppercase tracking-luxe">
        {["Details", "Delivery", "Payment"].map((s, i) => (
          <React.Fragment key={s}>
            <span className={`flex items-center gap-2 ${step >= i + 1 ? "text-brand-ink" : "text-muted-foreground"}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center ${step > i + 1 ? "bg-brand-pink" : step === i + 1 ? "bg-brand-ink text-white" : "bg-brand-blush"}`}>{step > i + 1 ? <Check className="w-3 h-3" /> : i + 1}</span>{s}
            </span>
            {i < 2 && <span className="flex-1 h-px bg-brand-border max-w-[60px]" />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12">
        <div>
          {step === 1 && (
            <div className="space-y-4 ck-fade-up" data-testid="checkout-step-details">
              <h2 className="font-serif-display text-2xl mb-2">Your details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full name" value={form.name} onChange={set("name")} testid="checkout-name" />
                <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="checkout-email" />
              </div>
              <Field label="Phone (optional)" value={form.phone} onChange={set("phone")} testid="checkout-phone" />
              <h2 className="font-serif-display text-2xl mt-6 mb-2">Delivery address</h2>
              <Field label="Address line 1" value={form.line1} onChange={set("line1")} testid="checkout-line1" />
              <Field label="Address line 2 (optional)" value={form.line2} onChange={set("line2")} testid="checkout-line2" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Town / City" value={form.city} onChange={set("city")} testid="checkout-city" />
                <Field label="Postcode" value={form.postcode} onChange={set("postcode")} testid="checkout-postcode" />
                <Field label="Country" value={form.country} onChange={set("country")} testid="checkout-country" />
              </div>
              <button onClick={() => canContinue ? setStep(2) : toast.error("Please complete the required fields.")} className="bg-brand-ink text-white px-8 py-3 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors mt-2" data-testid="to-delivery">Continue to delivery</button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 ck-fade-up" data-testid="checkout-step-delivery">
              <h2 className="font-serif-display text-2xl mb-2">Delivery options</h2>
              {freeDelivery && <p className="text-sm text-[#2D6A4F] mb-2">You qualify for free UK delivery — choose your speed.</p>}
              {delivery.options.map((o) => (
                <label key={o.id} className={`flex items-center justify-between border rounded-sm px-4 py-3 cursor-pointer ${method === o.id ? "border-brand-ink bg-brand-blush/40" : "border-brand-border"}`} data-testid={`delivery-${o.id}`}>
                  <div className="flex items-center gap-3">
                    <input type="radio" checked={method === o.id} onChange={() => setMethod(o.id)} className="accent-brand-pink" />
                    <div><p className="text-sm font-medium">{o.name}</p><p className="text-xs text-muted-foreground">{o.estimate}</p></div>
                  </div>
                  <span className="text-sm">{freeDelivery ? "Free" : gbp(o.price)}</span>
                </label>
              ))}
              <div className="mt-4">
                <label className="text-xs uppercase tracking-luxe">Order notes (optional)</label>
                <textarea value={form.notes} onChange={set("notes")} rows={3} placeholder="Gift message, colour requests, delivery instructions…" className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm mt-1 bg-white outline-none focus:border-brand-pink" data-testid="checkout-notes" />
              </div>
              <div className="flex gap-3 mt-2">
                <button onClick={() => setStep(1)} className="border border-brand-ink px-6 py-3 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-blush transition-colors">Back</button>
                <button onClick={() => setStep(3)} className="bg-brand-ink text-white px-8 py-3 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors" data-testid="to-payment">Continue to payment</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 ck-fade-up" data-testid="checkout-step-payment">
              <h2 className="font-serif-display text-2xl mb-2">Payment</h2>
              <div className="border border-brand-border rounded-sm p-5 flex items-center gap-3 bg-brand-blush/30">
                <Lock className="w-5 h-5 text-brand-pink" />
                <p className="text-sm text-muted-foreground">You'll be securely redirected to Stripe to complete payment. Use test card <strong>4242 4242 4242 4242</strong> in test mode.</p>
              </div>
              <label className="flex items-start gap-3 text-sm mt-3">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 accent-brand-pink" data-testid="agree-terms" />
                <span>I agree to the <Link to="/terms" className="underline">Terms & Conditions</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>, and understand that personalised / made-to-order items may be excluded from change-of-mind cancellation.</span>
              </label>
              <div className="flex gap-3 mt-2">
                <button onClick={() => setStep(2)} className="border border-brand-ink px-6 py-3 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-blush transition-colors">Back</button>
                <button onClick={placeOrder} disabled={submitting} className="flex-1 sm:flex-none bg-brand-ink text-white px-10 py-3 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors disabled:opacity-50" data-testid="place-order">{submitting ? "Redirecting…" : `Pay ${gbp(total)}`}</button>
              </div>
            </div>
          )}
        </div>

        {/* summary */}
        <div className="bg-brand-blush/40 rounded-sm p-7 h-fit">
          <h3 className="font-serif-display text-2xl mb-5">Order summary</h3>
          <div className="space-y-3 mb-5 max-h-60 overflow-y-auto">
            {cart.map((it, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <img src={it.image} alt={it.title} className="w-12 h-14 object-cover rounded-sm" />
                <div className="flex-1"><p className="leading-tight">{it.title}</p><p className="text-xs text-muted-foreground">{it.colour ? `${it.colour} · ` : ""}Qty {it.qty}</p></div>
                <span>{gbp(it.unit_price * it.qty)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mb-4">
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Discount code" className="flex-1 border border-brand-border rounded-sm px-3 py-2 text-sm bg-white outline-none focus:border-brand-pink" data-testid="discount-input" />
            <button onClick={applyCode} className="bg-brand-ink text-white px-4 rounded-sm text-sm hover:bg-brand-pink hover:text-brand-ink transition-colors" data-testid="apply-discount"><Tag className="w-4 h-4" /></button>
          </div>
          <div className="space-y-2 text-sm border-t border-brand-border pt-4">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{gbp(cartSubtotal)}</span></div>
            {discountAmt > 0 && <div className="flex justify-between text-brand-pink"><span>{discountLabel}</span><span data-testid="checkout-discount">−{gbp(discountAmt)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>{deliveryCost === 0 ? "Free" : gbp(deliveryCost)}</span></div>
            <div className="flex justify-between font-medium text-base border-t border-brand-border pt-2 mt-2"><span>Total</span><span data-testid="checkout-total">{gbp(total)}</span></div>
          </div>
          {freeDelivery && <p className="text-xs text-[#2D6A4F] mt-2">✓ Free UK delivery (order over £40)</p>}
          <p className="text-xs text-muted-foreground mt-4">All prices in GBP incl. applicable taxes.</p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-luxe">{label}</label>
      <input type={type} value={value} onChange={onChange} className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm mt-1 bg-white outline-none focus:border-brand-pink" data-testid={testid} />
    </div>
  );
}
